import { Pipe, PipeTransform } from '@angular/core';

/**
 * Pipe for QR users to display backend translations from message.transcripts.
 * Falls back to original message if target language translation is not available.
 *
 * Note: This pipe is impure to ensure it re-evaluates when the target language changes.
 * This is necessary because the component uses OnPush change detection and language
 * changes need to immediately reflect in all speech bubbles.
 */
@Pipe({
  name: 'backendTranslate',
})
export class BackendTranslatePipe implements PipeTransform {
  transform(
    messageText: string,
    transcripts: { [key: string]: string },
    sourceLanguage: string,
    targetLanguage: string,
    translationsDisabled: boolean
  ): string {
    // If translations are disabled, return original message
    if (translationsDisabled) {
      return messageText;
    }

    // Compare using short codes to handle "nl" vs "nl-NL" mismatches
    const targetShort = targetLanguage?.split('-')[0];
    const sourceShort = sourceLanguage?.split('-')[0];

    // If no target language or same as source, return original
    if (!targetLanguage || targetShort === sourceShort) {
      return messageText;
    }

    // Return backend translation if available, otherwise fallback to original
    return this.getTranscript(transcripts, targetLanguage) || messageText;
  }

  /**
   * Helper to find a transcript matching the target language.
   * Tries exact match first, then falls back to short code match.
   */
  private getTranscript(
    transcripts: { [key: string]: string },
    targetLang: string
  ): string | undefined {
    if (!transcripts || !targetLang) return undefined;

    // Try exact match first (e.g., 'en-US')
    if (transcripts[targetLang]) return transcripts[targetLang];

    // Try short code match (e.g., 'en' from 'en-US')
    const shortCode = targetLang.split('-')[0];
    const matchingKey = Object.keys(transcripts).find(
      (key) => key === shortCode || key.startsWith(shortCode + '-')
    );

    return matchingKey ? transcripts[matchingKey] : undefined;
  }
}
